import enviro from 'enviro';
import { getI18nText } from 'pricing-pages-ui-components/utils/languageUtils';
import { getIsQAProxy } from 'pricing-pages-lib/utils/proxyUtils';
import { getPublicSiteDomain } from 'pricing-pages-lib/utils/publicPageUrls';
import { useIsMobile } from 'pricing-pages-ui-components/hooks/useMedia';
import { NavOverrideStyles } from 'pricing-pages-ui-components/stylesheets/NavOverride.styles';
import getLocale from 'pricing-pages-ui/utils/getLocale';
import { useCallback, useEffect, useState } from 'react';

// Global nav types - extending existing window object
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
// This component exists so we can code split the react-nav
const BaseLayout = ({
  children,
  isLoggedIn,
  pathname
}) => {
  const [navScriptLoaded, setNavScriptLoaded] = useState(false);
  const [navCSSLoaded, setNavCSSLoaded] = useState(false);
  const isMobile = useIsMobile();

  // substring to remove country. Locale returns [language]-[country] like en-us
  const localeWithoutCountry = getLocale().substring(0, 2);

  // if locale is pt, set to pt-BR since global nav expects it
  const locale = localeWithoutCountry === 'pt' ? 'pt_BR' : localeWithoutCountry;
  const isQA = enviro.isQa() || getIsQAProxy();
  const NAV_SCRIPT_ID = 'hs-global-nav-script';
  const NAV_CSS_ID = 'hs-global-nav-css';
  const getCDNUrl = useCallback(() => {
    return isQA ? 'https://www.hubspot.com/assets/embed-nav/next/' : 'https://www.hubspot.com/assets/embed-nav/latest/';
  }, [isQA]);
  const onScriptLoad = useCallback(() => {
    setNavScriptLoaded(true);
  }, []);
  const onCSSLoad = useCallback(() => {
    setNavCSSLoaded(true);
  }, []);
  useEffect(() => {
    const cdnUrl = getCDNUrl();

    // Load CSS
    let navCSS = document.getElementById(NAV_CSS_ID);
    if (!navCSS) {
      navCSS = document.createElement('link');
      navCSS.rel = 'stylesheet';
      navCSS.href = `${cdnUrl}embed.css`;
      navCSS.id = NAV_CSS_ID;
      document.head.appendChild(navCSS);
    }

    // prevent duplicate listeners
    if (!navCSS.hasAttribute('data-listener-attached')) {
      navCSS.addEventListener('load', onCSSLoad);
      navCSS.setAttribute('data-listener-attached', 'true');
    }

    // Load Script
    let navScript = document.getElementById(NAV_SCRIPT_ID);
    if (!navScript) {
      navScript = document.createElement('script');
      navScript.async = true;
      navScript.src = `${cdnUrl}embed.js`;
      navScript.id = NAV_SCRIPT_ID;
      document.head.appendChild(navScript);
    }

    // prevent duplicate listeners
    if (!navScript.hasAttribute('data-listener-attached')) {
      navScript.addEventListener('load', onScriptLoad);
      navScript.setAttribute('data-listener-attached', 'true');
    }
    return () => {
      try {
        navScript.removeEventListener('load', onScriptLoad);
        navCSS.removeEventListener('load', onCSSLoad);

        // remove the listener
        navScript.removeEventListener('load', onScriptLoad);
        navCSS.removeEventListener('load', onCSSLoad);

        // remove the dom element too
        navScript.remove();
        navCSS.remove();

        // reset the state
        setNavScriptLoaded(false);
        setNavCSSLoaded(false);
      } catch (error) {
        console.warn('[BaseLayout] Error during cleanup', error);
      }
    };
  }, [getCDNUrl, onScriptLoad, onCSSLoad, isMobile]);
  useEffect(() => {
    if (navScriptLoaded && navCSSLoaded) {
      const domain = getPublicSiteDomain();

      // Configure HSGlobalNav
      window.HSGlobalNavConfig = {
        locale,
        cacheKey: `pricing-pages-nav-${locale}`,
        cacheTtl: 600000,
        // 10 minutes
        navMaxWidth: '95.5rem',
        languages: [{
          locale: 'en',
          name: 'English',
          url: 'https://www.hubspot.com/pricing'
        }, {
          locale: 'de',
          name: 'Deutsch',
          url: 'https://www.hubspot.de/pricing'
        }, {
          locale: 'es',
          name: 'Español',
          url: 'https://www.hubspot.es/pricing'
        }, {
          locale: 'pt_BR',
          name: 'Português',
          url: 'https://br.hubspot.com/pricing'
        }, {
          locale: 'fr',
          name: 'Français',
          url: 'https://www.hubspot.fr/pricing'
        }, {
          locale: 'ja',
          name: '日本語',
          url: 'https://www.hubspot.jp/pricing'
        }]
      };
      window.HSGlobalNavCustomizations = {
        isLoggedIn,
        logoDest: domain,
        cta1: {
          text: getI18nText('pricingPage.signupCtas.getStartedFreeConcise'),
          url: `${domain}/products/get-started`
        }
      };

      // Load the navigation
      try {
        var _HSGlobalNav, _HSGlobalNav$load;
        (_HSGlobalNav = window.HSGlobalNav) === null || _HSGlobalNav === void 0 || (_HSGlobalNav$load = _HSGlobalNav.load) === null || _HSGlobalNav$load === void 0 || _HSGlobalNav$load.call(_HSGlobalNav);
      } catch (error) {
        console.error('[BaseLayout] Error loading global nav', error);
      }
    }
  }, [isLoggedIn, locale, navScriptLoaded, navCSSLoaded, pathname]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(NavOverrideStyles, {}), /*#__PURE__*/_jsx("div", {
      className: "hs-global-nav hs-global-nav-header hs-global-nav-loading"
    }), children, /*#__PURE__*/_jsx("div", {
      className: "hs-global-nav hs-global-nav-footer hs-global-nav-loading"
    })]
  });
};
export default BaseLayout;